import ShowChartIcon from "@mui/icons-material/ShowChart";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import { Box, Typography } from "@mui/material";
import { AnimatePresence, motion, type Transition, useReducedMotion } from "framer-motion";
import type { DateTime } from "luxon";
import { type ChangeEvent, type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemSwitch } from "../../../components/UIToolkit/Clickable/SystemSwitch";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { MOTION } from "../../../theme/tokens";
import type { OtMode } from "../../../utils/models";
import type { ClampRange } from "../utils";
import {
	useGetMultiFacilityCostStats,
	useGetMultiFacilityDisciplinarySummary,
	useGetMultiFacilityHppdStats,
	useGetMultiFacilityKpiSummary,
	useGetMultiFacilityPerformanceOverTime,
	useGetMultiFacilityPpdStats,
	useGetMultiFacilityShiftPickups,
	usePortfolioWageAllowed,
} from "./hooks";
import { MultiFacilityChartCard } from "./MultiFacilityChartCard";
import { MultiFacilityDisciplinaryCard } from "./MultiFacilityDisciplinaryCard";
import { MultiFacilityGrid } from "./MultiFacilityGrid";
import { MultiFacilityKpiRow } from "./MultiFacilityKpiRow";
import { MultiFacilityPerformanceHeatmap } from "./MultiFacilityPerformanceHeatmap";
import { MultiFacilitySelector } from "./MultiFacilitySelector";
import { assignFacilityColors, buildFacilitySelectorGroups, costAxisTick, filterVisibleDepartmentIds, multiFacilityDepartmentIds } from "./utils";

type Props = {
	sinceDate: DateTime;
	endDate?: DateTime;
	isActual: boolean;
	laborCost: boolean;
	estimateMissingWage: boolean;
	/**
	 * The user's single-mode MC settings that change VALUES (late-scheduled shifts, weekly-OT day
	 * attribution, holiday-date removal, agency pickups), threaded through so multi numbers match
	 * the single view under the same settings. The multi header hides the settings menu — these
	 * are inherited from single mode, not editable here.
	 */
	includeLateScheduled?: boolean;
	otMode: OtMode;
	excludeHolidays?: boolean;
	excludeAgency?: boolean;
	clampRange?: ClampRange;
};

/** The multi-facility body has two views: the network metrics grid (default) and the charts. */
type MultiFacilityView = "grid" | "charts";

/**
 * Multi-facility Management Center body. Defaults to the network metrics grid (a read-only,
 * backoffice-style table of the network's facilities); the user toggles to the charts, which
 * compare the selector-chosen facilities against their own average (each chart's bold anchor is the
 * mean of the facilities drawn on it; only the KPI row stays portfolio-wide over ALL accessible
 * facilities). All six multi queries fire on entry for the FULL capped set (locked loading strategy
 * — facility selection is client-side only, zero refetch). The charts view renders the portfolio KPI
 * header row, the facility selector, six comparison line charts (the four cost-slot cards render only
 * when the portfolio wage gate allows) and the full-width disciplinary stacked-bar card.
 */
export const ManagementCenterMultiFacility: FC<Props> = ({
	sinceDate,
	endDate,
	isActual,
	laborCost,
	estimateMissingWage,
	includeLateScheduled,
	otMode,
	excludeHolidays,
	excludeAgency,
	clampRange,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	const portfolioWageAllowed = usePortfolioWageAllowed();
	const shouldReduceMotion = useReducedMotion();

	// The grid ⇄ charts switch is occasional, so a gentle opacity+slide crossfade is worth it to avoid a
	// jarring hard-swap. A soft ease-in-out with an unhurried enter reads as a calm, gentle transition;
	// the slide is dropped under reduced motion, keeping only the opacity fade that aids comprehension.
	// Timing/curve/slide come from the shared MOTION tokens (durations there are ms; Framer wants seconds).
	const viewEnterTransition: Transition = { duration: MOTION.DURATION.VIEW_ENTER / 1000, ease: MOTION.EASE_IN_OUT };
	const viewExitTransition: Transition = { duration: MOTION.DURATION.VIEW_EXIT / 1000, ease: MOTION.EASE_IN_OUT };
	const viewSlideY = shouldReduceMotion ? 0 : MOTION.VIEW_SLIDE_PX;

	// Grid is the default view; the user toggles to the existing charts. Rendered with the same
	// segmented pill (SystemFlatSwitch) as the sibling Single/All-Facilities toggle in the header.
	const [view, setView] = useState<MultiFacilityView>("grid");
	const isChartsView = view === "charts";
	const viewOptions: FlatSwitchOption<MultiFacilityView>[] = [
		{
			label: (
				<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
					<TableRowsOutlinedIcon sx={{ fontSize: 16 }} />
					{t("mc.multi.view.grid")}
				</Box>
			),
			value: "grid",
			"data-testid": "multi-facility-view-grid",
		},
		{
			label: (
				<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
					<ShowChartIcon sx={{ fontSize: 16 }} />
					{t("mc.multi.view.charts")}
				</Box>
			),
			value: "charts",
			"data-testid": "multi-facility-view-charts",
		},
	];
	// Defined once and rendered in whichever view is active — the charts top bar, or in grid view the
	// data grid's own toolbar — so it sits inline with each view's controls and stays flush top-right.
	const viewToggle = (
		<SystemFlatSwitch<MultiFacilityView>
			variant="outline"
			options={viewOptions}
			activeIndexOrValue={view}
			onSwitch={(option) => setView(option.value)}
			delayCallback={0}
			data-testid="multi-facility-view-toggle"
		/>
	);

	// The comparison spans only the CURRENT session's network — the facilities the user can access
	// whose `hospital` matches the chosen department's — not every network they belong to (a
	// cross-network admin shouldn't compare unrelated portfolios). Sorted for stable query keys and
	// deterministic per-facility colors; capped at the backend's hard per-request limit, with the
	// chosen department guaranteed to survive the cap — it's same-network by definition and the
	// default selection.
	const sortedDepartmentIds = useMemo(
		() => multiFacilityDepartmentIds(userInfo.departments, chosenDepartment.hospital, chosenDepartment.department_id),
		[userInfo.departments, chosenDepartment.hospital, chosenDepartment.department_id],
	);
	const colors = useMemo(() => assignFacilityColors(sortedDepartmentIds), [sortedDepartmentIds]);

	// Page-level facility selection: THE mechanism deciding which facilities the table, heatmap and
	// charts show. Defaults to EVERY accessible facility — the table has always shown the whole
	// network, and scoping it to the selection (so a row can remove itself) would otherwise silently
	// shrink it to one facility. Plain state (no storage, no URL), re-seeded whenever the capped
	// facility set or the chosen department changes — the same render-time seed-key idiom as the
	// chart cards' hidden-series seeding. Deliberately NOT part of any query key: narrowing or
	// widening the selection must never refetch.
	const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(sortedDepartmentIds);
	const selectionSeedKey = `${sortedDepartmentIds.join(",")}|${chosenDepartment.department_id}`;
	const selectionSeededKeyRef = useRef<string>(selectionSeedKey);
	if (selectionSeededKeyRef.current !== selectionSeedKey) {
		selectionSeededKeyRef.current = selectionSeedKey;
		setSelectedDepartmentIds(sortedDepartmentIds);
	}
	// Every path that changes the selection goes through here, so the analytics event has exactly one
	// producer. `source` distinguishes the dropdown from a row's remove control and from Clear all —
	// previously only the dropdown tracked, and the other two were invisible.
	const changeSelection = useCallback(
		(nextSelectedIds: string[], source: "selector" | "row_remove" | "clear_all") => {
			track("management_center_multi_facility_selection_changed", {
				selected_count: nextSelectedIds.length,
				total_count: sortedDepartmentIds.length,
				all_selected: nextSelectedIds.length === sortedDepartmentIds.length,
				source,
			});
			setSelectedDepartmentIds(nextSelectedIds);
		},
		[track, sortedDepartmentIds.length],
	);
	// Removing the last visible facility would leave every view in its empty state with no obvious
	// way back, so a row's remove control stops at one facility; Clear all still allows emptying the
	// comparison deliberately.
	const removeFacility = useCallback(
		(departmentId: string) => {
			const next = selectedDepartmentIds.filter((id) => id !== departmentId);
			if (next.length === 0) return;
			changeSelection(next, "row_remove");
		},
		[selectedDepartmentIds, changeSelection],
	);
	// Minimizing the table lifts the heatmap into its place; state lives here because both are
	// siblings in the grid view.
	const [isTableMinimized, setIsTableMinimized] = useState(false);
	const toggleTableMinimized = useCallback(() => setIsTableMinimized((previous) => !previous), []);
	const visibleDepartmentIds = useMemo(
		() => filterVisibleDepartmentIds(sortedDepartmentIds, selectedDepartmentIds),
		[sortedDepartmentIds, selectedDepartmentIds],
	);
	// Grouped by region (derived from each department's timezone) so a long network list is scannable.
	const selectorGroups = useMemo(
		() => buildFacilitySelectorGroups(sortedDepartmentIds, userInfo.departments, t),
		[sortedDepartmentIds, userInfo.departments, t],
	);
	const allSelected = visibleDepartmentIds.length === sortedDepartmentIds.length;

	// The average anchor spans all facilities by default (a stable network baseline); this toggle
	// re-scopes it to the currently drawn lines. Will move into the MC settings menu later.
	const [averageActiveLinesOnly, setAverageActiveLinesOnly] = useState(false);
	const onAverageScopeToggle = (event: ChangeEvent<HTMLInputElement>) => {
		const activeLinesOnly = event.target.checked;
		setAverageActiveLinesOnly(activeLinesOnly);
		track("management_center_multi_average_scope_toggled", { active_lines_only: activeLinesOnly });
	};

	const commonArgs = { departmentIds: sortedDepartmentIds, sinceDate, endDate };
	const settingsArgs = { includeLateScheduled, otMode, excludeHolidays };
	const wageArgs = { ...commonArgs, ...settingsArgs, isActual, laborCost, estimateMissingWage };

	const { data: hppdData, isLoading: isLoadingHppd, isError: isErrorHppd } = useGetMultiFacilityHppdStats({ ...commonArgs, ...settingsArgs, isActual });
	const { data: ppdData, isLoading: isLoadingPpd, isError: isErrorPpd } = useGetMultiFacilityPpdStats(wageArgs);
	const { data: costData, isLoading: isLoadingCost, isError: isErrorCost } = useGetMultiFacilityCostStats(wageArgs);
	const {
		data: pickupsData,
		isLoading: isLoadingPickups,
		isError: isErrorPickups,
	} = useGetMultiFacilityShiftPickups({ ...commonArgs, excludeAgency, excludeHolidays });
	const { data: disciplinaryData, isLoading: isLoadingDisciplinary, isError: isErrorDisciplinary } = useGetMultiFacilityDisciplinarySummary(commonArgs);
	const { data: kpiData, isError: isErrorKpi } = useGetMultiFacilityKpiSummary(wageArgs);
	const { data: performanceData, isLoading: isLoadingPerformance, isError: isErrorPerformance } = useGetMultiFacilityPerformanceOverTime(wageArgs);
	// Per-facility wage restriction, straight from the performance response (the endpoint reads live
	// permissions; the client's department list is a cached login snapshot that can go stale).
	const wageRestrictedDepartmentIds = useMemo(() => new Set(performanceData?.wageRestrictedDepartmentIds ?? []), [performanceData]);

	useEffect(() => {
		track("view_management_center_multi", {
			facilities: sortedDepartmentIds.length,
			date_from: sinceDate.toISODate(),
			date_to: endDate?.toISODate(),
			is_actual_data_shown: isActual,
		});
	}, [track, sortedDepartmentIds.length, sinceDate, endDate, isActual]);

	// Everything the line-chart cards share; each card adds its own series + query states.
	// `sortedDepartmentIds` (colors, dedupe, hidden-series seed key) stays the FULL capped set;
	// `visibleDepartmentIds` only decides which facility series get BUILT.
	const commonCardProps = {
		sortedDepartmentIds,
		visibleDepartmentIds,
		currentDepartmentId: chosenDepartment.department_id,
		colors,
		clampRange,
		averageActiveLinesOnly,
		shrinkLegendForSmallScreens: true,
	};
	const facilitySelectionControls = (
		<>
			<SystemButton
				buttonVariant="borderless"
				size="small"
				disabled={selectedDepartmentIds.length === 0}
				onClick={() => changeSelection([], "clear_all")}
				noFullWidthMobile
				data-testid="mc-multi-clear-all"
				sx={{ fontSize: "14px" }}
			>
				{t("mc.multi.selector.clear.all")}
			</SystemButton>
			<MultiFacilitySelector
				groups={selectorGroups}
				selectedIds={selectedDepartmentIds}
				onChange={(next) => changeSelection(next, "selector")}
				totalCount={sortedDepartmentIds.length}
			/>
		</>
	);

	return (
		<Box data-testid="management-center-multi-root" sx={{ position: "relative", width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
			{/* popLayout (not "wait") so the entering view mounts in parallel with the outgoing one instead
			    of after it. The charts are expensive to render (~6 recharts cards); with "wait" that render
			    happened during a blank gap after the table left. popLayout keeps the old view on screen while
			    the new one renders, so the switch reads as instant even though the charts take time to paint. */}
			<AnimatePresence mode="popLayout" initial={false}>
				{isChartsView ? (
					<motion.div
						key="charts"
						style={{ width: "100%" }}
						initial={{ opacity: 0, y: viewSlideY }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -viewSlideY, transition: viewExitTransition }}
						transition={viewEnterTransition}
					>
						<Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
							{/* Charts top bar: heading on the left; facility controls and the view toggle on the
							    right. Each view owns its own top row, so the toggle stays flush top-right across both. */}
							<Box sx={{ width: "100%", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", columnGap: 1, rowGap: 0.5 }}>
								<Box>
									<Typography
										data-testid="management-center-multi-heading"
										sx={{ fontWeight: 700, fontSize: { xl: "20px", xs: "16px" }, color: "primary.dark" }}
									>
										{allSelected
											? t("all.facilities.count", { count: sortedDepartmentIds.length })
											: t("all.facilities.partial.count", { selected: visibleDepartmentIds.length, count: sortedDepartmentIds.length })}
									</Typography>
									{!allSelected && (
										<Typography data-testid="mc-multi-kpi-scope-note" variant="caption" sx={{ color: "text.secondary" }}>
											{t("mc.multi.kpi.scope.note", { count: sortedDepartmentIds.length })}
										</Typography>
									)}
								</Box>
								<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", columnGap: 1.5, rowGap: 0.5 }}>
									<SystemSwitch
										checked={averageActiveLinesOnly}
										onChange={onAverageScopeToggle}
										label={t("mc.multi.average.active.only")}
										labelPlacement="start"
										dataTestId="mc-multi-average-scope-toggle"
									/>
									{facilitySelectionControls}
									{viewToggle}
								</Box>
							</Box>
							<Box sx={{ width: "100%", display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 1 }}>
								<MultiFacilityKpiRow data={kpiData} isError={isErrorKpi} />
								<MultiFacilityChartCard
									title={t("hppd.over.time")}
									dataTestId="mc-multi-hppd-chart"
									series={hppdData}
									facilityNames={hppdData?.facilityNames}
									isLoading={isLoadingHppd}
									isError={isErrorHppd}
									{...commonCardProps}
								/>
								{portfolioWageAllowed && (
									<>
										<MultiFacilityChartCard
											title={t("ppd.over.time")}
											dataTestId="mc-multi-ppd-chart"
											series={ppdData}
											facilityNames={ppdData?.facilityNames}
											isLoading={isLoadingPpd}
											isError={isErrorPpd}
											tickRenderer={costAxisTick}
											{...commonCardProps}
										/>
										<MultiFacilityChartCard
											title={t("total.cost.over.time")}
											dataTestId="mc-multi-cost-chart"
											series={costData?.total}
											facilityNames={costData?.facilityNames}
											isLoading={isLoadingCost}
											isError={isErrorCost}
											tickRenderer={costAxisTick}
											{...commonCardProps}
										/>
										<MultiFacilityChartCard
											title={t("agency.cost.over.time")}
											dataTestId="mc-multi-agency-chart"
											series={costData?.agency}
											facilityNames={costData?.facilityNames}
											isLoading={isLoadingCost}
											isError={isErrorCost}
											tickRenderer={costAxisTick}
											{...commonCardProps}
										/>
										<MultiFacilityChartCard
											title={t("overtime.cost.over.time")}
											dataTestId="mc-multi-overtime-chart"
											series={costData?.overtime}
											facilityNames={costData?.facilityNames}
											isLoading={isLoadingCost}
											isError={isErrorCost}
											tickRenderer={costAxisTick}
											{...commonCardProps}
										/>
									</>
								)}
								<MultiFacilityChartCard
									title={t("shift.pickups.over.time")}
									dataTestId="mc-multi-pickups-chart"
									series={pickupsData}
									facilityNames={pickupsData?.facilityNames}
									isLoading={isLoadingPickups}
									isError={isErrorPickups}
									{...commonCardProps}
								/>
								<MultiFacilityDisciplinaryCard
									data={disciplinaryData}
									isLoading={isLoadingDisciplinary}
									isError={isErrorDisciplinary}
									sortedDepartmentIds={sortedDepartmentIds}
									visibleDepartmentIds={visibleDepartmentIds}
								/>
							</Box>
						</Box>
					</motion.div>
				) : (
					<motion.div
						key="grid"
						style={{ width: "100%" }}
						initial={{ opacity: 0, y: viewSlideY }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -viewSlideY, transition: viewExitTransition }}
						transition={viewEnterTransition}
					>
						<Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
							<MultiFacilityGrid
								anchorDepartmentId={chosenDepartment.department_id}
								departmentIds={sortedDepartmentIds}
								visibleDepartmentIds={visibleDepartmentIds}
								sinceDate={sinceDate}
								endDate={endDate}
								onRemoveFacility={removeFacility}
								isMinimized={isTableMinimized}
								onToggleMinimized={toggleTableMinimized}
								trailingNode={
									<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end", columnGap: 1.5, rowGap: 0.5 }}>
										{facilitySelectionControls}
										{viewToggle}
									</Box>
								}
							/>
							<MultiFacilityPerformanceHeatmap
								data={performanceData}
								isLoading={isLoadingPerformance}
								isError={isErrorPerformance}
								isActual={isActual}
								ppdAllowed={portfolioWageAllowed}
								selectedDepartmentIds={visibleDepartmentIds}
								currentDepartmentId={chosenDepartment.department_id}
								onRemoveFacility={removeFacility}
								wageRestrictedDepartmentIds={wageRestrictedDepartmentIds}
							/>
						</Box>
					</motion.div>
				)}
			</AnimatePresence>
		</Box>
	);
};
